import axios from "axios"
import { groupProductsByTerminals } from "./mappers"
import { convertLoad, convertCalendarLoad } from "utils/"
import moment from "moment-timezone"
import { LOAD } from "types"
import dayjs from "dayjs"
import { toast } from "react-toastify"
import _sortBy from "lodash/sortBy"

export const fetchDetails =
  (
    endpoint: string,
    options?: {
      pageSize?: string
      query?: string
      queryMandatory?: boolean
      mapper?: Function
    }
  ) =>
  async (props: any) => {
    if (options?.queryMandatory && !options?.query) return
    const url_query = new URLSearchParams(props.queryKey[1]).toString()
    let url = `${endpoint}/?page=1&page_size=${
      options?.pageSize ?? 50
    }&${url_query}&${options?.query}`
    if (props.pageParam) url = props.pageParam.split("/api").pop() || url
    const { data } = await axios.get(url)
    if (options?.mapper) return options.mapper(data)
    return data
  }

export const fetchEndpoint = (endpoint: string) => async () => {
  return (await axios.get(endpoint)).data
}

export const betterFetch =
  (endpoint: string, options?: any) =>
  async ({ pageParam }: any) => {
    const url = (pageParam ?? endpoint).replace("http:", "https:")

    // Avoid predefined queries as react-query is handling pagingation
    if (url.includes("&page=")) {
      options.query = {}
    }

    const { data } = await axios.get(url, {
      params: options?.query ?? {},
      // params: pageParam ? {} : options?.query ?? {}
    })
    return options?.mapper ? options.mapper(data) : data
  }

const status_map = {
  9: { sortCode: 0, text: "Rejected" },
  0: { sortCode: 1, text: "Unscheduled" },
  1: { sortCode: 2, text: "Paused" },
  4: { sortCode: 3, text: "Loaded" },
  5: { sortCode: 3, text: "Pre-loaded" },
  3: { sortCode: 4, text: "Active" },
  2: { sortCode: 5, text: "Scheduled" },
  8: { sortCode: 6, text: "Proposed" },
  7: { sortCode: 7, text: "Completed" },
} as any

export const deleteObject =
  (endpoint: string, client?: any) => async (id: number | string) => {
    if (!id) return
    await axios.delete(`${endpoint}/${id}/`)
    client && client.invalidateQueries(endpoint)
  }

export const fetchObjectAtEndpoint =
  (endpoint: string, id: string | number | undefined) => async () => {
    if (!id) return
    return (await axios.get(`${endpoint}/${id}`)).data
  }

export const fetchSingleLoad = (id?: string) => async () => {
  if (!id) return
  const load = (await axios.get(`event/loads/${id}/status`)).data
  return convertLoad(load)
}

export const getLoadForCreate = (id: string | undefined) => async () => {
  if (!id) return
  const data = (await axios.get(`loads/${id}`)).data
  const load = convertLoad(data)
  return {
    driver: load?.driver?.id,
    priority: load.priority,
    customer: load.deliveries?.[0]?.customer.id,
    location: load.deliveries?.[0]?.location.id,
    delivery_start_time: load.delivery_start_time,
    delivery_end_time: load.delivery_end_time,
  }
}

export const fetchExpectedProducts = (id: string | undefined) => async () => {
  if (!id) return
  return (await axios.get(`pickups/${id}/expected-product`)).data.results
}

export const createExpectedProducts =
  (pickupId: number | string, products: any) => async () => {
    products.forEach(async (product: any) => {
      await axios.post("/expected-products/", {
        gross_quantity: product.gross_quantity,
        product: product.product,
        pickup: pickupId,
      })
    })
  }

export const fetchLoadPickups =
  (id?: string | number, mapper?: Function) => async () => {
    if (!id) return
    const { data } = await axios.get(`/pickups/?load=${id}`)
    return data?.results ?? []
  }

type LoadData = {
  file_key?: any
  driver: number
  priority?: number
  freight_only?: boolean
  po_number?: any
  bo_notes?: string //back office notes
  delivery_start_time: string
  delivery_end_time: string
  confirmation_required?: boolean
  accessorials?: any[]
  assigned_salesperson?: {
    id: number
    full_name: string
  }
  delivery_notes: string
  delivery_products: any
  driverData?: { is_common_carrier: boolean }
  customer: number
  location: number
  floating_price?: boolean
  silent_update?: boolean
}

export const createLoad = async (
  data: LoadData,
  flags?: any,
  isCustomer?: boolean
) => {
  const eventBody = {
    silent_update: data?.silent_update ?? false,
    driver_id: data.driver,
    priority: data.priority,
    freight_only: !!data.freight_only,
    confirmation_required: !!data.confirmation_required,
    delivery_start_time: moment
      .utc(data.delivery_start_time)
      .format("YYYY-MM-DD HH:mm:ss"),
    delivery_end_time: moment
      .utc(data.delivery_end_time)
      .format("YYYY-MM-DD HH:mm:ss"),
    notes: [] as any[],
    pickups: [] as any[],
    deliveries: [] as any[],
    floating_price: data?.floating_price ?? false,
  }

  if (data.bo_notes) {
    eventBody.notes = [{ description: data.bo_notes }]
  }

  // create deliveries
  if (data?.delivery_products.length && data.customer) {
    const deliveryBody = {
      customer_id: data.customer,
      notes: data.delivery_notes,
      note_image: data.file_key ?? undefined,
      location_id: data.location,
      po_number: data.po_number?.target?.value,
      accessorial_items: data.accessorials,
      assigned_salesperson: data.assigned_salesperson?.id,
      products: data.delivery_products.map((product: any) => {
        const price =
          product.actualValue && !isNaN(product.actualValue)
            ? { price: Number(product.actualValue) }
            : product.price && !isNaN(product.price)
            ? { price: Number(product.price) }
            : {}
        const cost_basis = product?.cost_basis ?? null
        return {
          product_id: product.product,
          tanks:
            product.tank_id === undefined
              ? undefined
              : [
                  {
                    tank_id: product.tank_id,
                    expected_gross_quantity: Number(product.gross_quantity),
                  },
                ],
          expected_gross_quantity: Number(product.gross_quantity),
          ...price,
          cost_basis: cost_basis
            ? (Math.round(cost_basis * 10000) / 10000)?.toFixed(4)
            : null,
        }
      }),
    }
    eventBody.deliveries.push(deliveryBody)
  }

  const terminalsProducts = groupProductsByTerminals(
    data.delivery_products,
    true
  )

  for (const terminal in terminalsProducts) {
    const pickupBody = {
      products: terminalsProducts[terminal].map((product: any) => ({
        preferences: product.preferences,
        suppliers: product.suppliers,
        product_id: product.product,
        expected_gross_quantity: Number(product.gross_quantity),
      })),
    } as any
    // if (terminal !== 'undefined') pickupBody.terminal_id = Number(terminal)
    // Create pickup
    eventBody.pickups.push(pickupBody)
  }
  // return
  const newLoad = await axios.post("event/loads/", {
    type: "CREATE_LOAD",
    data: eventBody,
  })

  await eventApi("MATCH_PICKUP_DELIVERY", newLoad.data.id, {})

  const preferences = [] as any[]
  const loadId = newLoad.data.id
  eventBody.pickups.forEach((pickup: any, pickupIndex: number) => {
    pickup.products.forEach(async (product: any, productIndex: number) => {
      const pickup_id = `p-${loadId}-${pickupIndex + 1}`
      product.preferences.forEach((pref: any) => {
        preferences.push({
          pickup_id,
          ...pref,
          loading_number: undefined,
        })
      })
    })
  })

  // await axios.post('/event/loads/preferences/', {
  //   load_id: loadId,
  //   preferences
  // })

  if (data.driver) {
    const start = moment
      .utc(data.delivery_start_time)
      .format("YYYY-MM-DD HH:mm:ss")
    const { prefillScheduleTime } = flags

    const responseData: any = await scheduleLoad(
      newLoad.data.id,
      start,
      data.driver,
      prefillScheduleTime,
      !!data?.driverData?.is_common_carrier
    )
    if (responseData?.error?.message) return responseData
  }

  if (isCustomer) {
    toast.success(`Load created LD-${newLoad.data.id}`, {
      position: "top-center",
      autoClose: 6000,
      theme: "dark",
    })
  } else {
    toast.success(`Load created LD-${newLoad.data.id}. Click to view`, {
      position: "top-center",
      autoClose: 6000,
      theme: "dark",
      onClick: () => window.open(`/new_loads/${newLoad.data.id}`, "_blank"),
    })
  }

  return newLoad.data
}

export const createDelivery = async (data: any, load_id: string) => {
  // create deliveries
  if (!data?.delivery_products.length) return
  if (!data.customer) return

  await axios.post("deliveries/", {
    delivery_notes: data.delivery_notes,
    delivery_products: data.delivery_products,
    load: load_id,
    customer: data.customer,
    location: data.location,
  })

  const terminalsProducts = groupProductsByTerminals(data.delivery_products)

  for (const terminal in terminalsProducts) {
    // create pickups
    const body = {
      expected_products: terminalsProducts[terminal],
      load: load_id,
      terminal,
    }
    await axios.post("/pickups/", body)
  }
}

export const fetchBols = (id: number | string | undefined) => async () => {
  const bills = {} as any
  if (!id) return bills
  const res = (await axios.get(`pickups/${id}/bols`)).data.results
  res.forEach((result: any) => {
    const products = result.pickup_products ?? []
    products.forEach((product: any) => {
      const id = product.product_id
      bills[id] = bills[id] ?? []
      bills[id].push({
        gross_quantity: product.gross_quantity,
        file_url: result.file_url,
        name: result?.name,
        bol_id: result.id,
        suppler: result.supplier,
        supplier_label: result.supplier_label,
      })
    })
  })

  return bills
}

export const loadSortMethod = (load: any) => {
  if (load?.issue) return -2
  if (load?.priority) return -1
  return status_map[load?.status_code || 0].sortCode
}

export const fetchLoads =
  (offset?: number) =>
  async ({ pageParam = "" }) => {
    offset = offset ?? 0
    const date = dayjs().add(offset, "days").format("YYYY-MM-DD")
    const url = `event/calendar/loads/?date=${date}&src=right`
    // if (pageParam) url = pageParam.split('/api').pop() || url
    const { data } = await axios.get(url)
    // Map it to old method
    return remapDispatchLoads(data)
    const loads = _sortBy(data.map(convertCalendarLoad), loadSortMethod)
    const testLoads = {} as any
    loads.map((load: any) => {
      if (testLoads[load.status_code]) return
      testLoads[load.status_code] = load.status
    })
    return loads
  }

export const remapDispatchLoads = (data: any) => {
  const loads = _sortBy((data ?? []).map(convertCalendarLoad), loadSortMethod)
  const testLoads = {} as any
  loads.map((load: any) => {
    if (testLoads[load.status_code]) return
    testLoads[load.status_code] = load.status
  })
  return loads
}

export const togglePause = async (
  load: LOAD,
  proposeRef: any,
  popupRef?: any,
  callback?: Function
) => {
  if (load.status_code !== 1) {
    if (popupRef) {
      popupRef.open(
        "Pause Load",
        "Are you sure you want to pause the load",
        async () => {
          await axios.post(`event/loads/`, {
            type: "PAUSE_LOAD",
            load_id: load.id,
            data: {},
          })
          callback?.()
        }
      )
      return
    }
    await axios.post(`event/loads/`, {
      type: "PAUSE_LOAD",
      load_id: load.id,
      data: {},
    })
    callback?.()
    return
  }

  await axios.post(`event/loads/`, {
    type: "UNPAUSE_LOAD",
    load_id: load.id,
    data: {},
  })

  if (load.driver?.id && load.start_time) {
    proposeRef?.propose?.(
      load.id,
      load.driver.id,
      load.start_time,
      load.end_time,
      null,
      callback
    )

    return
  }

  await axios.post(`event/loads/`, {
    type: "UNSCHEDULE_LOAD",
    load_id: load.id,
    data: {},
  })
}

export const cleanPickups = async (load: any) => {
  for (const pickup of load.pickups) {
    if ((pickup?.products ?? []).length < 1) {
      await axios.post("event/loads/", {
        type: "DELETE_PICKUP",
        load_id: load.id,
        data: {
          pickup_id: pickup.id,
        },
      })
    }
  }
  await eventApi("MATCH_PICKUP_DELIVERY", load.id)
}

export const cleanLoads = async (load: any) => {
  const products = load.pickups.flatMap((pickup: any) => pickup.products)
  const groupedProducts = groupProductsByTerminals(products)

  const pickups = [] as any[]
  const preferences = [] as any[]
  Object.entries(groupedProducts).forEach(
    ([terminal, products], index: number) => {
      pickups.push({
        terminal_id: isNaN(parseFloat(terminal))
          ? undefined
          : parseFloat(terminal),
        products: (products ?? ([] as any)).map((p: any) => ({
          product_id: p.product.id,
          expected_gross_quantity: p.expected_gross_quantity,
          gross_quantity: p.gross_quantity ? p.gross_quantity : undefined,
        })),
      })
      const newPrefs = (products ?? ([] as any)).flatMap((p: any, i: number) =>
        (p.preferences ?? []).map((pref: any) => ({
          pickup_id: `p-${load.id}-${index + 1}`,
          pickup_product_id: `p-${load.id}-${index + 1}-${i + 1}`,
          product_id: p.product.id,
          ...pref,
          loading_number: undefined,
        }))
      )

      preferences.push(...newPrefs)
    }
  )

  if (!Object.keys(pickups).length) return
  eventApi("UPDATE_LOAD", load.id, { pickups })
  await eventApi("MATCH_PICKUP_DELIVERY", load.id)

  await axios.post("/event/loads/preferences/", {
    load_id: load.id,
    preferences,
  })
  // groupProductsByTerminals
}

const matchPickupApi = async (loadId: string | number | undefined) => {
  return await axios.get(`pickup-delivery-similarity-match/${loadId}/`)
}

export const eventApi = async (
  type: string,
  load_id: string | number | undefined,
  data?: any
) => {
  // Hack or smart fix 🤔 to reroute MATCH_PICKUP_DELIVERY to another api
  // if (type === "MATCH_PICKUP_DELIVERY") return await matchPickupApi(load_id)
  if (type === "MATCH_PICKUP_DELIVERY") return

  const body = { type, data: data || {} } as any
  if (load_id) body.load_id = load_id
  return await axios.post("event/loads/", body)
}

export const moveToNextDay = async (load: any) => {
  const data = {} as any
  if (load.delivery_start_time)
    data.delivery_start_time = moment
      .utc(load.delivery_start_time)
      .add(1, "days")
      .toISOString()
  if (load.start_time)
    data.start_time = moment
      .utc(load.start_time)
      .add(1, "days")
      .format("YYYY-MM-DD HH:mm:ss")
  if (load.delivery_end_time)
    data.delivery_end_time = moment
      .utc(load.delivery_end_time)
      .add(1, "days")
      .toISOString()
  if (load.end_time)
    data.end_time = moment
      .utc(load.end_time)
      .add(1, "days")
      .format("YYYY-MM-DD HH:mm:ss")

  await eventApi("UPDATE_LOAD", load.id, data)
  await eventApi("MATCH_PICKUP_DELIVERY", load.id, {})
}

export const makeEventApi =
  (type: string, load_id: string, data?: any) => async () => {
    return await eventApi(type, load_id, data)
  }

export const downloadTicket = async (load_id: string, delivery_id: string) => {
  return await axios
    .get(`event/loads/${load_id}/delivery/${delivery_id}/pdf`)
    .then((data: any) => {
      const pdf = data?.data?.data
      const title = data?.data?.title

      const linkSource = `data:application/pdf;base64,${pdf}`
      const alink = document.createElement("a")
      alink.href = linkSource
      alink.download = title
      alink.click()
    })
}

export const downloadTickets = async (
  deliveries: string[],
  isDeliveryAssets?: boolean,
  interceptBypassCode?: number
) => {
  if (!deliveries?.length) return alert("There are no deliveries")
  return await axios
    .post(
      `loads/delivery_tickets?interceptBypassCode=${interceptBypassCode}`,
      { deliveries, user_uploaded: isDeliveryAssets ?? undefined },
      {
        responseType: "blob",
      }
    )
    .then((data: any) => {
      if (
        !data?.data?.type?.includes("application/pdf") &&
        !data?.response?.data?.type?.includes("application/pdf")
      ) {
        return {
          error: data?.response?.status === 422 ? 422 : "no_pdf_download",
        }
      }
      const pdf = data?.data
      const title = isDeliveryAssets
        ? "Delivery Assets.pdf"
        : "Bulk Export Tickets.pdf"
      const url = window.URL.createObjectURL(pdf)
      const alink = document.createElement("a")
      alink.href = url
      alink.download = title
      alink.click()
    })
}

export const downloadBols = async (
  loads: string[],
  interceptBypassCode?: number
) => {
  return await axios
    .post(
      `loads/bols?interceptBypassCode=${interceptBypassCode}`,
      { loads },
      {
        responseType: "blob",
      }
    )
    .then((data: any) => {
      if (
        !data?.data?.type?.includes("application/pdf") &&
        !data?.response?.data?.type?.includes("application/pdf")
      ) {
        return {
          error: data?.response?.status === 422 ? 422 : "no_pdf_download",
        }
      }
      const pdf = data?.data ?? data?.response?.data
      const title = "Bulk Export Bols.pdf"
      const url = window.URL.createObjectURL(pdf)
      const alink = document.createElement("a")
      alink.href = url
      alink.download = title
      alink.click()
      return {}
    })
}

export const downloadBoL = async (
  load_id: string,
  bol_id: string,
  name: string
) => {
  const { data } = await axios.get(`/loads/${load_id}/bols/${bol_id}/`, {
    responseType: "blob",
  })
  const url = window.URL.createObjectURL(data)
  const a = document.createElement("a")
  a.href = url
  a.download = `${name}.pdf`
  a.click()
}

export const downloadInvoice = async (load_id: string, delivery_id: string) => {
  return await axios
    .get(`invoice/loads/${load_id}/delivery/${delivery_id}/pdf`)
    .then((data: any) => {
      const pdf = data?.data?.data
      const title = data?.data?.title
      const linkSource = `data:application/pdf;base64,${pdf}`
      const alink = document.createElement("a")
      alink.href = linkSource
      alink.download = title
      alink.click()
    })
}

// Inventory Management stuff

export const fetchTanks =
  (
    date: string,
    sortBy: string,
    size: number | null | undefined,
    user_details?: any
  ) =>
  async () => {
    date = dayjs(date).utc().toISOString()
    const { data } = await axios.get(
      `/tank-details/1/all?page_size=${size ?? ""}&date=${date}`
    )
    const sites = {} as any
    const tanks = {} as any
    let results = data.results ?? data

    if (
      user_details?.allowed_customer ||
      user_details?.allowed_locations?.length
    ) {
      // location is in tank.location.id and customer is in tank.location.customer.id
      results = results.filter((item: any) => {
        const location_id = item?.tank?.location?.id
        const customer_id = item?.tank?.location?.customer?.id
        if (
          user_details?.allowed_customer &&
          user_details?.allowed_customer === customer_id
        )
          return true
        return user_details?.allowed_locations?.includes(location_id)
      })
    }
    if (!sortBy)
      results.sort((a: any, b: any) => b.status - a.status || a.id - b.id)
    if (sortBy === "min_runout")
      results.sort((a: any, b: any) =>
        dayjs(a.runout_time).isBefore(b.runout_time) ? -1 : 1
      )

    if (sortBy === "max_average_sales")
      results.sort((a: any, b: any) =>
        a.average_sales_trend < b.average_sales_trend ? 1 : -1
      )

    results.map((item: any) => {
      const tank_id = item?.tank?.id
      if (tanks[tank_id]) return
      tanks[tank_id] = item

      const location_id = `_${item?.tank.location.id}`
      sites[location_id] = sites[location_id] ?? {
        site: item.tank.location,
        tanks: [],
      }

      sites[location_id].tanks.push(item)
    })

    return { tanks, sites }
  }

export const fetchSites =
  (
    date: string,
    sortBy: string,
    mapper?: Function,
    params?: {
      page_size: number
      location?: string
    }
  ) =>
  async () => {
    const endpoint = date
      ? "/sites/volumes/"
      : `/sites/${
          params?.page_size
            ? `?page_size=${params?.page_size}&paginate=True&location_name=${params?.location}`
            : ""
        }`
    const { data } = await axios.get(endpoint, {
      params: {
        date: date || undefined,
        order: sortBy,
      },
    })
    if (mapper) return mapper(data)
    return data
  }

export const scheduleLoad = async (
  loadId: string,
  startTime?: string,
  driverId?: any,
  prefillTime?: boolean,
  onlyPropose?: boolean,
  plannerMode?: boolean
) => {
  // Get load details
  const load = await fetchSingleLoad(`${loadId}`)()
  // Make a list of all delivery location

  const deliveryLocations = (load?.deliveries ?? []).map(
    (delivery: any) => delivery?.location?.id
  )

  const pickupTerminals = (load?.pickups ?? []).map(
    (pickup: any) => pickup?.terminal?.id
  )

  const distances = await axios.get("/freight/site-distances/max", {
    params: {
      delivery_locations: deliveryLocations
        .filter((x: any) => Boolean(x))
        .join(","),
      terminals: pickupTerminals.filter((x: any) => Boolean(x)).join(","),
    },
  })
  // const site_distances_minutes = distances.data.reduce(
  //   (acc: number, dist: any) => acc + dist.max_time,
  //   0
  // )

  // const site_distances_minutes = 141
  // Get site distance details for every delivery location
  // Make a list of all pickup terminals
  const finalMinutes = distances?.data?.total_travel_time ?? 120
  // load.pickups.length * 45 +
  // (deliveryLocations.length - 1) * 20 +
  // load.deliveries.length * 45 +
  // (pickupTerminals.length - 1) * 20 +
  // site_distances_minutes

  // const dummyStartTime = startTime
  // console.log("DUMMY START =", dummyStartTime.format())
  // console.log("endTime =", endTime.format())
  const startTimeDayJs = dayjs(startTime, "YYYY-MM-DD HH:mm:ss")
  const endTime = startTimeDayJs.add(finalMinutes, "minutes")
  const fullEndTime = dayjs
    .utc(load.delivery_end_time)
    .format("YYYY-MM-DD HH:mm:ss")
  const fullStartTime = dayjs
    .utc(load.delivery_start_time)
    .format("YYYY-MM-DD HH:mm:ss")
  const loadPayload = {
    start_time: prefillTime ? fullStartTime : startTime,
    end_time: prefillTime ? fullEndTime : endTime.format("YYYY-MM-DD HH:mm:ss"),
    driver_id: driverId,
  } as any

  if (!!plannerMode) loadPayload.silent_update = true

  const type = onlyPropose ? "PROPOSE_LOAD" : "SCHEDULE_LOAD"
  try {
    const response: any = await axios.post(
      `event/loads/?interceptBypassMessage=cannotscheduleanunapprovedload`,
      {
        type,
        load_id: loadId,
        data: loadPayload,
      }
    )
    if (response?.error) return response
  } catch (error) {}
}

export const blobToJson = (blob: Blob) => {
  return new Promise((resolve, reject) => {
    const reader: any = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result)
        resolve(json)
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = () => {
      reject(reader.error)
    }
    reader.readAsText(blob)
  })
}

export const timezoneString = Intl.DateTimeFormat().resolvedOptions().timeZone
